import type { Classificacao, Motivo } from '@profitlens/calculo';
import { Check, AlertTriangle, HelpCircle } from 'lucide-react';

/**
 * Padrão 4: a classificação nunca vem sozinha.
 *
 * Nenhuma tela mostra só o selo. Vêm junto TODOS os critérios avaliados — os
 * atendidos e os não atendidos — com o identificador da regra (RN-093). Você
 * consegue discordar do sistema com argumento.
 *
 * Sobre a cor de DADOS_INSUFICIENTES: ela é neutra de propósito. Não é uma
 * oportunidade ruim, é uma que ainda não foi medida. Pintar de vermelho
 * ensinaria o olho a descartá-la junto com as inviáveis, e negócios bons se
 * perderiam por falta de cadastrar uma alíquota.
 *
 * Cor também nunca é o único sinal: o texto por extenso acompanha sempre.
 */
const ESTILOS: Record<Classificacao, { rotulo: string; classe: string }> = {
  EXCELENTE: { rotulo: 'Excelente', classe: 'text-c-exc bg-c-exc/12 border-c-exc' },
  VIAVEL: { rotulo: 'Viável', classe: 'text-c-via bg-c-via/12 border-c-via' },
  VIAVEL_COM_RESSALVAS: { rotulo: 'Viável com ressalvas', classe: 'text-c-res bg-c-res/12 border-c-res' },
  MARGEM_INSUFICIENTE: { rotulo: 'Margem insuficiente', classe: 'text-c-mar bg-c-mar/12 border-c-mar' },
  INVIAVEL: { rotulo: 'Inviável', classe: 'text-c-inv bg-c-inv/12 border-c-inv' },
  DADOS_INSUFICIENTES: { rotulo: 'Dados insuficientes', classe: 'text-c-dad bg-c-dad/12 border-c-dad' },
  REQUER_REVISAO: { rotulo: 'Requer revisão', classe: 'text-c-rev bg-c-rev/12 border-c-rev' },
};

export function SeloClassificacao({
  valor,
  tamanho = 'md',
}: {
  valor: Classificacao;
  tamanho?: 'sm' | 'md';
}) {
  const e = ESTILOS[valor];
  const dim = tamanho === 'sm' ? 'px-2 py-0.5 text-[0.62rem]' : 'px-3 py-1 text-[0.7rem]';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-mono font-semibold tracking-wider uppercase ${dim} ${e.classe}`}
    >
      {e.rotulo}
    </span>
  );
}

export function ListaMotivos({ motivos }: { motivos: Motivo[] }) {
  if (motivos.length === 0) return null;
  const atendidos = motivos.filter((m) => m.atende).length;
  const ressalvas = motivos.length - atendidos;

  return (
    <div className="flex flex-col gap-2">
      <p className="font-mono text-[0.7rem] text-tinta-3">
        {atendidos} {atendidos === 1 ? 'critério atendido' : 'critérios atendidos'}
        {ressalvas > 0 && ` · ${ressalvas} ${ressalvas === 1 ? 'ressalva' : 'ressalvas'}`}
      </p>
      <ul className="flex flex-col gap-1.5 text-sm">
        {motivos.map((m) => (
          <li key={m.regra} className="grid grid-cols-[1.1rem_4.2rem_1fr] items-baseline gap-2">
            <span aria-hidden>
              {m.atende ? (
                <Check className="size-3.5 text-c-via" />
              ) : m.indeterminado ? (
                <HelpCircle className="size-3.5 text-c-dad" />
              ) : (
                <AlertTriangle className="size-3.5 text-c-res" />
              )}
            </span>
            <span className="font-mono text-[0.7rem] text-tinta-3">{m.regra}</span>
            <span className={m.atende ? 'text-tinta-2' : 'text-tinta'}>
              <span className="sr-only">{m.atende ? 'Atende: ' : 'Ressalva: '}</span>
              {m.texto}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CartaoClassificacao({
  valor,
  motivos,
}: {
  valor: Classificacao;
  motivos: Motivo[];
}) {
  return (
    <section className="flex flex-col gap-3 rounded-md border border-regua-2 bg-superficie p-4">
      <div className="flex flex-wrap items-center gap-3">
        <SeloClassificacao valor={valor} />
        {valor === 'DADOS_INSUFICIENTES' && (
          <span className="text-sm text-tinta-2">
            Não é uma oportunidade ruim — é uma que ainda não foi medida.
          </span>
        )}
      </div>
      <ListaMotivos motivos={motivos} />
    </section>
  );
}
